import { useGraphQLQuery } from '@betterment/graphql';
import { Card, Dialog, H2, Icon, ListRow, Separator, Text, VStack } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';

import { SambaAutoLink } from '~/b4b/components/SambaAutoLink';
import { useCurrentEmployerId } from '~/b4b/utils/currentEmployer';
import { urlBuilder } from '~/b4b/utils/urlBuilder';

import { AdditionalBenefitsDocument } from './graphql/query.additionalBenefits.generated';

const baseUnstyledButtonStyles = {
  all: 'unset',
  width: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  cursor: 'pointer',
  _hover: {
    color: 'inherit',
    textDecoration: 'underline',
  },
  _focusVisible: {
    outlineWidth: 'thick',
    outlineColor: 'border.focused',
    outlineStyle: 'solid',
    outlineOffset: '1px',
  },
};

export const AdditionalBenefitsCard: React.FC = () => {
  const employerId = useCurrentEmployerId();
  const { data } = useGraphQLQuery(AdditionalBenefitsDocument, {
    variables: { employerId },
    throwOnError: true,
  });

  if (!data || !data.employer) {
    return null;
  }

  const hasActiveFiveTwoNineBenefit = data.employer.fiveTwoNineBenefit
    ? data.employer.fiveTwoNineBenefit.status === 'ACTIVE'
    : false;
  const hasActiveFinancialCoachingBenefit = data.employer.financialCoachingBenefit
    ? data.employer.financialCoachingBenefit.status === 'ACTIVE'
    : false;

  if (!hasActiveFiveTwoNineBenefit && !hasActiveFinancialCoachingBenefit) {
    return null;
  }

  return (
    <Card css={{ p: 'space.300' }}>
      <Card.Header css={{ marginBottom: 'space.300' }}>
        <H2 variant="title200">Additional benefits</H2>
      </Card.Header>
      <Separator css={{ marginBottom: 'space.300' }} />
      <Card.Content>
        <VStack css={{ gap: 'space.100' }}>
          {hasActiveFiveTwoNineBenefit && (
            <SambaAutoLink.Unstyled to={urlBuilder.fiveTwoNine()} css={{ width: '100%' }}>
              <ListRow css={{ p: 'space.0' }}>
                <ListRow.LeadingContent>
                  <Text className="underlineOnHover">529 education savings</Text>
                </ListRow.LeadingContent>
                <ListRow.TrailingAsset>
                  <Icon name="CaretRight" size="small" />
                </ListRow.TrailingAsset>
              </ListRow>
            </SambaAutoLink.Unstyled>
          )}
          {hasActiveFinancialCoachingBenefit && (
            <Dialog>
              <Dialog.Trigger asChild={true}>
                <button type="button" className={css(baseUnstyledButtonStyles)}>
                  <ListRow css={{ p: 'space.0', width: '100%' }}>
                    <ListRow.LeadingContent>
                      <Text className="underlineOnHover">Financial coaching</Text>
                    </ListRow.LeadingContent>
                    <ListRow.TrailingAsset>
                      <Icon name="CaretRight" size="small" />
                    </ListRow.TrailingAsset>
                  </ListRow>
                </button>
              </Dialog.Trigger>
              <Dialog.Content css={{ p: 'space.400' }} size="large">
                <Dialog.Title>Support employees with financial coaching</Dialog.Title>
                <Text as="p" css={{ mb: 'space.400' }}>
                  Give employees access to 1:1 financial advice and planning sessions with financial experts to help
                  them achieve their goals with their 401(k) and beyond.
                  <ul>
                    <li>Employees will receive personalized advice with a licensed specialist.</li>
                    <li>Service is covered by your company and free for employees.</li>
                    <li>Sessions are unlimited.</li>
                  </ul>
                </Text>

                <SambaAutoLink
                  to={urlBuilder.getCoaching()}
                  variant="solidSecondary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more
                </SambaAutoLink>
              </Dialog.Content>
            </Dialog>
          )}
        </VStack>
      </Card.Content>
    </Card>
  );
};
